/**
 * The key set every language must provide, exactly: English is the reference,
 * and each other language is typed against it, so a missing or stray key is a
 * compile error rather than a raw key showing up on screen.
 */
export type Translations = Record<keyof typeof en, string>;

export const en = {
   'components.language.label': 'Language',
   'components.footer.policy': 'Privacy Policy',
   'components.login-link.home': 'Home',
   'components.login-link.login': 'Login',
   'components.login-link.logout': 'Logout',
   'pages.login-page.login': 'Login',
   'pages.login-page.register': 'Register',
   'pages.login-page.signInWithGoogle': 'Sign in with Google',
   'pages.login-page.orEmail': 'or with email',
   'pages.login-page.email': 'Email address',
   'pages.login-page.password': 'Password',
   'pages.login-page.noAccount': 'No account yet?',
   'pages.login-page.haveAccount': 'Already have an account?',
   'pages.login-page.errorWrongPassword': 'Wrong email or password.',
   'pages.login-page.errorEmailInUse': 'This email is already registered.',
   'pages.login-page.errorWeakPassword': 'Password must be at least 6 characters.',
   'pages.login-page.errorGeneral': 'Login error. Please try again.',
   'pages.login-page.forgotPassword': 'Forgot password?',
   'pages.forgot-password-page.title': 'Forgot Password',
   'pages.forgot-password-page.description': 'Enter your email address and we will send you a link to reset your password.',
   'pages.forgot-password-page.email': 'Email address',
   'pages.forgot-password-page.submit': 'Send reset link',
   'pages.forgot-password-page.success': 'Password reset email sent. Please check your inbox.',
   'pages.forgot-password-page.errorInvalidEmail': 'Invalid or unknown email address.',
   'pages.forgot-password-page.errorGeneral': 'Error. Please try again.',
   'pages.forgot-password-page.backToLogin': 'Back to login',
   'components.navbar.cookies.policy': 'With using this site, you agree to the use of cookies.',
   'components.navbar.cookies.accept': 'Accept',
   'components.navbar.menu': 'Menu',
   'components.navbar.darkTheme': 'Dark theme',
   'components.navbar.myShops': 'My shops',
   'pages.index-page.welcome': 'Search your customers will actually use',
   'pages.index-page.subtitle': 'Smart search for your online shop. It understands Latin, typos and “under 50”.',
   'pages.index-page.try.label': 'Your shop’s address or product feed',
   'pages.index-page.try.submit': 'Try it on my shop',
   'pages.index-page.try.reading': 'Reading your catalogue… this can take up to a minute.',
   'pages.index-page.try.ready': 'We read {{ count }} products from {{ name }}. Search them the way your customers would:',
   'pages.index-page.try.searchPlaceholder': 'e.g. a product name, with a typo, “under 50”',
   'pages.index-page.try.noResults': 'Nothing found for this search.',
   'pages.index-page.try.popular': 'Popular',
   'pages.index-page.try.examples': 'Try:',
   'pages.index-page.try.similarButton': 'Similar',
   'pages.index-page.try.similar': 'Similar to “{{ title }}”',
   'pages.index-page.try.similarHint': 'This is how <div data-oopsy-similar> shows them on the product page.',
   'pages.index-page.try.total': '{{ count }} found',
   'pages.index-page.try.under': 'up to {{ price }}',
   'pages.index-page.try.install': 'Put it on my shop',
   'pages.index-page.try.another': 'Try another shop',
   'pages.index-page.outOfStock': 'Out of stock',
   'errors.invalid-url': 'That doesn’t look like a web address.',
   'errors.unreachable': 'We could not open this address. Check it and try again.',
   'errors.blocked': 'The site blocks automated reading. Paste your product feed instead, e.g. the one you give Google Shopping.',
   'errors.no-products': 'We found no products there. Paste your product feed link instead.',
   'errors.too-many-shops': 'You can add up to 5 shops.',
   'errors.general': 'Something went wrong. Please try again.',
   'pages.index-page.how.title': 'How it works',
   'pages.index-page.how.step1': 'Type your shop’s address',
   'pages.index-page.how.step2': 'Search your own products',
   'pages.index-page.how.step3': 'Add it with one line of code',
   'pages.shops-page.loading': 'Loading your shops',
   'pages.shops-page.title': 'My shops',
   'pages.shops-page.loginRequired': 'Log in to add your shop.',
   'pages.shops-page.add': 'Add shop',
   'pages.shops-page.empty': 'No shops yet. Add your shop’s address above.',
   'pages.shops-page.products': '{{ count }} products',
   'pages.shops-page.active': 'Search box active',
   'pages.shops-page.notInstalled': 'Not installed yet',
   'pages.shop-page.status.working': 'Search is live',
   'pages.shop-page.status.waiting': 'Waiting for install',
   'pages.shop-page.status.products': '{{ count }} products',
   'pages.shop-page.insights.title': 'What customers search for',
   'pages.shop-page.settings.title': 'Shop and plan',
   'pages.shop-page.plan.title': 'Plan',
   'pages.shop-page.install.title': 'Install',
   'pages.shop-page.install.text': 'Paste this line just before </body> on every page of {{ host }}. It takes over your existing search box.',
   'pages.shop-page.install.custom': 'If your search box isn’t picked up, add data-input="#id-of-your-search-field" to the line.',
   'pages.shop-page.install.copy': 'Copy',
   'pages.shop-page.install.copied': 'Copied',
   'pages.shop-page.install.active': 'Active: last search from your site {{ date }}.',
   'pages.shop-page.install.waiting': 'Waiting for the first search from your site.',
   'pages.shop-page.try.title': 'Try it',
   'pages.shop-page.catalogue.title': 'Catalogue',
   'pages.shop-page.catalogue.text': '{{ count }} products from {{ source }}, read {{ date }}. Read again every night.',
   'pages.shop-page.catalogue.refresh': 'Read again now',
   'pages.shop-page.catalogue.error': 'The last refresh failed. Your search still uses the catalogue read {{ date }}.',
   'pages.shop-page.source.feed': 'your feed',
   'pages.shop-page.source.shopify': 'Shopify',
   'pages.shop-page.source.woocommerce': 'WooCommerce',
   'pages.shop-page.source.sitemap': 'your product pages',
   'pages.shop-page.top.title': 'Most searched',
   'pages.shop-page.notFound.title': 'Searched for, not found',
   'pages.shop-page.notFound.hint': 'What customers wanted and your search could not show them.',
   'pages.shop-page.stats.empty': 'No searches from your site yet.',
   'pages.shop-page.stats.times': '{{ count }}×',
   'pages.shop-page.remove': 'Remove shop',
   'pages.shop-page.removeConfirm': 'Remove this shop? The search box on your site stops working.',
   'pages.index-page.try.understood': 'Searching for: {{ what }}',
   'pages.shop-page.plan.free': 'Free plan',
   'pages.shop-page.plan.pro': 'Pro',
   'pages.shop-page.plan.freeText': 'Everything included: up to {{ count }} products and {{ searches }} searches a month, catalogue read again every week.',
   'pages.shop-page.plan.proText': 'Up to {{ count }} products, unlimited searches, catalogue read again every night.',
   'pages.shop-page.plan.upgrade': 'Get Pro',
   'pages.shop-page.notFound.locked': '{{ count }} different searches found nothing. The list is in Pro.',
   'pages.shop-page.recommendations.title': 'Recommendations',
   'pages.shop-page.recommendations.text': 'Put this element on your product page where you want “Similar products”. The line from “Install” must be on the page too.',
   'pages.shop-page.recommendations.picks': 'Most chosen products',
   'pages.shop-page.recommendations.picksHint': 'What customers open from the results and from similar products. Search ranks them a little higher.',
   'pages.shop-page.recommendations.locked': '“Similar products” on every product page, and the most chosen products when a search finds nothing, are in Pro.',
   'pages.shop-page.catalogue.textManual': '{{ count }} products from {{ source }}, read {{ date }}.',
   'pages.shop-page.catalogue.textWeekly': '{{ count }} products from {{ source }}, read {{ date }}. Read again every week.',
   'pages.shop-page.plan.paused': 'This month’s searches are used up: the AI is paused until the 1st. Plain search keeps working.',
   'pages.shop-page.plan.usage': 'This month: {{ used }} of {{ limit }} searches from your site.',
   'pages.shop-page.catalogue.nextRefresh': 'You can read it again from {{ date }}.',
   'errors.feed-needed': 'The free plan reads a product feed, Shopify or WooCommerce. Paste your product feed link, or ask for Pro.',
   'errors.refresh-too-soon': 'On the Free plan the catalogue can be read by hand once a day.',
};
